import type { RoleCode } from '../permissions/index.js';

/**
 * Écrans des maquettes (ecrans/design/*.dc.html) et les routes du contrat dont chacun a besoin.
 * Un écran se rend uniquement avec les mocks : ses lectures et ses actions sont servies par MSW (critère de fin L01).
 */
export interface Ecran {
  id: string;
  titre: string;
  role: RoleCode;
  /** Autres rôles servis par le même écran (ex. X15 : mandant et héritier). */
  roles?: readonly RoleCode[];
  lectures: readonly string[];
  actions: readonly string[];
}

/** Tous les rôles servis par l'écran, rôle principal compris. */
export const audience = (e: Ecran): readonly RoleCode[] => [e.role, ...(e.roles ?? []).filter((r) => r !== e.role)];

export const ECRANS = [
  {
    id: 'D01',
    titre: 'Tableau de bord',
    role: 'dirigeante',
    lectures: ['dossiers.pilotage', 'decisions.lister', 'echeances.lister', 'comptesRendus.aTraiter'],
    actions: ['decisions.trancher'],
  },
  {
    id: 'D02',
    titre: 'File du jour',
    role: 'assistante',
    lectures: ['taches.fileDuJour', 'relances.plans', 'messagerie.fils', 'documents.attendus'],
    actions: ['taches.modifier', 'relances.pause'],
  },
  {
    id: 'D03',
    titre: 'Dossier, vue d’ensemble',
    role: 'assistante',
    lectures: [
      'dossiers.lire',
      'procedure.etapes',
      'echeances.lister',
      'dossiers.journal',
      'comptesRendus.lister',
      'actesNotaries.lister',
      'documents.lister',
      'videos.lister',
      'temps.dossier',
    ],
    actions: [
      'temps.enregistrer',
      'temps.corriger',
      'procedure.ajouter',
      'procedure.sauter',
      'procedure.reordonner',
      'dossiers.modifier',
      'comptesRendus.creer',
      'comptesRendus.generer',
      'comptesRendus.modifier',
      'comptesRendus.valider',
      'actesNotaries.creer',
      'actesNotaries.modifier',
      'actesNotaries.retirer',
      'genealogie.contacts.apercu',
      'genealogie.contacts.importer',
      'videos.rattacher',
      'videos.retirer',
      'videos.partager',
      'videos.partage.retirer',
    ],
  },
  {
    id: 'D04',
    titre: 'Dossier, arbre et tableaux',
    role: 'assistante',
    lectures: ['genealogie.arbre', 'genealogie.devolution', 'genealogie.tableaux.exporter'],
    // Couloir G, point 5 : le blocage « mandat signé nécessaire » se force (dirigeante) ou se signale (assistante).
    actions: ['genealogie.personnes.creer', 'genealogie.relations.creer', 'genealogie.fratrie.ajouter', 'genealogie.options.enregistrer', 'demarrage.forcer', 'demarrage.demanderDeblocage'],
  },
  { id: 'D04b', titre: 'Arbre en cinq dispositions', role: 'assistante', lectures: ['genealogie.arbre'], actions: [] },
  {
    id: 'D04c',
    titre: 'Estimation des parts',
    role: 'dirigeante',
    lectures: ['estimations.lire', 'biens.lister', 'beta.questionsDues'],
    actions: ['beta.repondre'],
  },
  {
    id: 'D05',
    titre: 'Fiche héritier',
    role: 'assistante',
    lectures: [
      'genealogie.personne.lire',
      'confidentialite.consentements',
      'confidentialite.sensibles',
      'confidentialite.destinataires',
      'relances.plans',
      'documents.attendus',
      'confidentialite.notes',
    ],
    actions: [
      'genealogie.personne.modifier',
      'confidentialite.sensibles.partager',
      'messagerie.envoyer',
      'relances.pause',
      'confidentialite.notes.ajouter',
    ],
  },
  {
    id: 'D06',
    titre: 'Boîte unifiée',
    role: 'assistante',
    lectures: ['messagerie.fils', 'messagerie.fil', 'messagerie.apercuLien'],
    actions: ['messagerie.rattacher', 'messagerie.envoyer', 'documents.televerser'],
  },
  {
    id: 'D07',
    titre: 'Relances et modèles',
    role: 'assistante',
    lectures: ['relances.plans', 'modeles.lister'],
    actions: ['relances.simuler', 'relances.pause', 'relances.reprise', 'modeles.versionner'],
  },
  {
    id: 'D08',
    titre: 'Facturation du dossier',
    role: 'dirigeante',
    // dossiers.lire : sonde d'existence quand facturation et collecte répondent 404 (écart C10 : sans règles ou sans signature,
    // l'écran reste ouvert ; seul un dossier inexistant est « introuvable »).
    lectures: [
      'facturation.dossier',
      'facturation.collecte',
      'facturation.supplements.lister',
      'compensations.dossier',
      'dossiers.lire',
      'facturation.prestations.lister',
      'facturation.devis.lister',
      'facturation.devis.pdf',
      'facturation.factures.pdf',
    ],
    actions: [
      'facturation.regles',
      'facturation.echeancier',
      'paiements.liens',
      'facturation.factures.emettre',
      'facturation.supplements.ajouter',
      'facturation.supplements.retirer',
      'facturation.devis.creer',
    ],
  },
  {
    id: 'D09',
    titre: 'Documents (GED)',
    role: 'assistante',
    lectures: ['documents.lister', 'documents.lire', 'documents.attendus', 'mandats.lister'],
    actions: ['documents.examiner', 'documents.televerser', 'mandats.envoyer'],
  },
  {
    id: 'D10',
    titre: 'Administration et « voir en tant que »',
    role: 'admin',
    lectures: ['admin.utilisateurs', 'admin.etudes', 'admin.parametresLegaux', 'beta.reglages', 'impersonation.historique'],
    actions: [
      'impersonation.demarrer',
      'impersonation.terminer',
      'admin.utilisateurs.creer',
      'admin.etudes.creer',
      'admin.etudes.modifier',
      'admin.etudes.supprimer',
      'admin.parametresLegaux.versionner',
    ],
  },
  {
    id: 'D15',
    titre: 'Fiche titre de propriété',
    role: 'assistante',
    roles: ['dirigeante'],
    lectures: ['biens.lister', 'documents.lire', 'documents.lister'],
    actions: ['biens.creer', 'documents.examiner'],
  },
  // Constat C-21 (26/10) : l'entretien d'accueil de l'assistante (lot L63) n'était joignable par aucune adresse. Écran de bureau
  // (D) pour figurer dans la navigation de l'assistante et de la dirigeante ; RFC-L63-001 proposait M08, exclu du bandeau du bureau.
  {
    id: 'D16',
    titre: 'Entretien d’accueil',
    role: 'assistante',
    roles: ['dirigeante'],
    // Couloir G, point 6 : le vocal se dépose au dossier (et se transcrit si un service est configuré).
    lectures: ['dossiers.lire', 'accueil.vocaux.lister'],
    actions: ['genealogie.personnes.creer', 'genealogie.relations.creer', 'accueil.vocaux.ouvrir', 'accueil.vocaux.morceau', 'accueil.vocaux.terminer'],
  },
  // L64 (30/10, vocaux d'Isabelle du 28/10) : démarrage guidé d'un dossier de régularisation successorale, onglet du dossier.
  {
    id: 'D17',
    titre: 'Démarrage guidé',
    role: 'assistante',
    roles: ['dirigeante'],
    // Juge du 30/10 : le dossier est nommé en tête (U2) ; l'étape 21 s'appuie sur les mandats réellement au dossier (U12).
    lectures: ['demarrage.lire', 'dossiers.lire', 'mandats.lister', 'genealogie.arbre'],
    actions: ['demarrage.marquer', 'demarrage.repondre', 'demarrage.forcer', 'demarrage.demanderDeblocage', 'demarrage.cocher'],
  },
  // Couloir G, point 1 (30/10, décision de Joel) : poste de pilotage du mode guidé, choisi après la connexion.
  {
    id: 'D18',
    titre: 'Mode guidé',
    role: 'assistante',
    roles: ['dirigeante'],
    lectures: ['demarrage.tableau', 'demarrage.mode.lire', 'demarrage.lire', 'mandats.lister'],
    actions: ['dossiers.creer', 'demarrage.mode.choisir', 'demarrage.marquer', 'demarrage.forcer', 'demarrage.demanderDeblocage'],
  },
  {
    id: 'M01',
    titre: 'Décisions et alertes',
    role: 'dirigeante',
    lectures: ['decisions.lister', 'echeances.lister'],
    actions: ['decisions.trancher', 'messagerie.valider'],
  },
  {
    id: 'M02',
    titre: 'File du jour et appel en cours',
    role: 'assistante',
    lectures: ['taches.fileDuJour', 'genealogie.personne.lire'],
    actions: ['taches.modifier', 'sync.push'],
  },
  {
    id: 'M03',
    titre: 'Fiche héritier rapide et envoi d’un modèle',
    role: 'agent',
    lectures: ['genealogie.personne.lire', 'modeles.lister'],
    actions: ['messagerie.envoyer'],
  },
  {
    id: 'M04',
    titre: 'Empreinte de progression et action unique',
    role: 'heritier',
    lectures: ['portail.accueil'],
    actions: ['portail.pieces.demander'],
  },
  { id: 'M05', titre: 'Envoyer une pièce par photo', role: 'heritier', lectures: ['portail.accueil'], actions: ['portail.pieces.deposer'] },
  { id: 'M06', titre: 'Régler ma quote-part', role: 'heritier', lectures: ['portail.paiement', 'portail.prestations'], actions: [] },
  {
    id: 'M07',
    titre: 'Mes données et ma confidentialité',
    role: 'heritier',
    lectures: ['portail.confidentialite', 'portail.journal'],
    actions: ['portail.confidentialite.modifier', 'confidentialite.demandes'],
  },
  {
    id: 'X11',
    titre: 'Espace clerc',
    role: 'clerc',
    lectures: ['clerc.dossiers', 'documents.lister', 'notaire.interrogations'],
    actions: ['notaire.interrogations.modifier', 'documents.televerser'],
  },
  { id: 'X12', titre: 'Espace notaire', role: 'notaire', lectures: ['notaire.dossier', 'documents.lister'], actions: [] },
  {
    id: 'X13',
    titre: 'Portail du mandant',
    role: 'mandant',
    lectures: ['portail.accueil', 'portail.paiement', 'portail.journal', 'portail.branche', 'portail.videos'],
    actions: [],
  },
  {
    id: 'X14',
    titre: 'Portail de l’héritier',
    role: 'heritier',
    lectures: ['portail.accueil', 'portail.branche', 'portail.videos'],
    actions: ['portail.pieces.deposer', 'portail.pieces.demander', 'portail.confidentialite.modifier'],
  },
  {
    id: 'X15',
    titre: 'Mon arbre',
    role: 'mandant',
    roles: ['heritier'],
    lectures: ['portail.branche', 'portail.confidentialite'],
    actions: ['portail.confidentialite.modifier'],
  },
] as const satisfies readonly Ecran[];

/**
 * Écrans du contrat sans maquette dans ecrans/design, chacun avec sa raison. Le test des maquettes les accepte nommément, et
 * échoue dès qu'une maquette apparaît pour l'un d'eux (il faut alors le retirer d'ici).
 */
export const ECRANS_SANS_MAQUETTE: Readonly<Record<string, string>> = {
  D16: 'Entretien d’accueil : composant Accueil du lot L63 construit et testé sans maquette (RFC-L63-001) ; rendu joignable pour le constat C-21. Maquette à produire.',
  D18: 'Mode guidé : poste de pilotage simple du couloir G (choix du mode à la connexion), sur le modèle visuel du prototype de référence (cartes indicateurs, sections arrondies). Maquette à produire.',
  D17: 'Démarrage guidé : onglet du dossier du lot L64, construit sur le déroulé décrit par Isabelle (vocaux du 28/10), dans le style des écrans voisins. Maquette à produire.',
};
